import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../i18n/types';
import type { Language } from '../i18n/types';
import { useEffect } from 'react';

interface LanguageSwitcherProps {
  isMobile?: boolean;
}

export default function LanguageSwitcher({
  isMobile = false,
}: LanguageSwitcherProps) {
  const { i18n } = useTranslation();

  const changeLanguage = (lang: Language) => {
    i18n.changeLanguage(lang);
    // Update HTML lang attribute for SEO
    document.documentElement.lang = lang;
  };

  const currentLanguage = i18n.language as Language;

  useEffect(() => {
    if (currentLanguage !== 'zh-HK' && currentLanguage !== 'en') {
      i18n.changeLanguage('zh-HK');
    }
  }, [currentLanguage, i18n]);

  if (isMobile) {
    // Mobile version: Display as buttons in hamburger menu
    return (
      <div className='flex flex-col gap-2 pt-4 mt-4 border-t border-gray-700'>
        {LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={`text-left px-4 py-2 rounded transition-colors ${
              currentLanguage === lang.code
                ? 'bg-yellow-400 text-gray-900 font-bold'
                : 'text-white hover:bg-gray-800'
            }`}
          >
            {lang.nativeLabel}
          </button>
        ))}
      </div>
    );
  }

  // Desktop version: Compact toggle buttons
  return (
    <div className='flex items-center gap-1 bg-gray-900 bg-opacity-50 rounded-full p-1'>
      {LANGUAGES.map((lang) => (
        <button
          key={lang.code}
          onClick={() => changeLanguage(lang.code)}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${
            currentLanguage === lang.code
              ? 'bg-yellow-400 text-gray-900 shadow-lg'
              : 'text-white hover:bg-gray-800'
          }`}
          aria-label={`Switch to ${lang.label}`}
        >
          {lang.nativeLabel}
        </button>
      ))}
    </div>
  );
}
